import DocumentPage from './DocumentPage';

export const toDosDocument = {
  slug: 'to-dos',
  route: '/to-dos',
  title: 'To-Dos',
  category: 'Collaboration',
  format: 'DOCX',
  description:
    'The EduX To-Dos module helps teams create tasks, assign responsibility, schedule activities as deadlines, track progress, and complete work through time-based Kanban states.',
  metrics: [
    { label: 'Primary focus', value: 'Task management' },
    { label: 'Core records', value: 'Tasks and activities' },
    { label: 'Planning model', value: 'Inbox to later' },
  ],
  workflow: [
    'Create a new task from the To-Dos workspace and assign it to the right user.',
    'Schedule an activity to set the deadline and manage follow-up actions.',
    'Move the task through the time-based Kanban states and mark it done when completed.',
  ],
  sections: [
    {
      id: 'introduction',
      title: 'Introduction',
      body: [
        'The EduX To-Do Module is designed to manage tasks, track activities, and improve productivity within an organization. It allows users to create, assign, monitor, and complete tasks efficiently.',
        'This module is especially useful for teams, departments, and individuals to organize daily work, deadlines, and responsibilities.',
      ],
      detailCards: [
        {
          title: 'Purpose of the To-Do Module',
          bullets: [
            'To manage daily tasks efficiently.',
            'To assign tasks to users or employees.',
            'To track task progress.',
            'To set deadlines and priorities.',
            'To improve team collaboration.',
          ],
        },
      ],
    },
    {
      id: 'creating-a-new-task',
      title: 'Creating a New Task',
      body: [
        'Click on the New button and enter the task details such as task title and assigned user.',
        'The task is created in Inbox first so it can be organized, assigned, and scheduled later.',
      ],
      figure: {
        src: '/assets/doc-images/todo/image.png',
        alt: 'To-Dos new task creation in Inbox',
        caption: 'Creating a new task in the To-Dos module from the Inbox stage.',
      },
      steps: [
        'Click the New button in the To-Dos module.',
        'Enter the task title, such as Prepare Payroll Report.',
        'Add or adjust the assigned user if needed.',
        'Save the task so it appears in Inbox.',
      ],
    },
    {
      id: 'task-with-deadline',
      title: 'Task with Deadline (Activity Scheduling)',
      body: [
        'This screen shows the task after scheduling an activity. The system displays the deadline as Due in 5 days.',
        'In the To-Do module, the deadline is managed using Activities instead of a direct field.',
      ],
      gallery: [
        {
          src: '/assets/doc-images/todo/image2.png',
          alt: 'Schedule activity form for To-Do deadline',
          caption: 'Scheduling an activity with due date and assigned user for the task.',
        },
        {
          src: '/assets/doc-images/todo/image3.png',
          alt: 'Task with planned activity and due in 5 days indicator',
          caption: 'Task after scheduling an activity, with deadline visibility and available actions.',
        },
      ],
      subsections: [
        {
          id: 'task-actions',
          title: 'Task Actions',
          body: [
            'Users can perform the following actions after scheduling a task activity:',
          ],
          bullets: [
            'Mark Done: to complete the task.',
            'Edit: to modify the task.',
            'Cancel: to remove the scheduled activity.',
          ],
        },
      ],
    },
    {
      id: 'completed-task',
      title: 'Completed Task',
      body: [
        'The completed-task view shows the task after it has been marked as finished. The task Prepare Payroll Report was successfully completed by clicking Mark Done.',
        'Completed tasks are no longer part of the active working flow and remain available for reference.',
      ],
      figure: {
        src: '/assets/doc-images/todo/image4.png',
        alt: 'Completed task in done state',
        caption: 'Task after completion with the Done state visible.',
      },
      bullets: [
        'The task is shown as completed in the Done state.',
        'Completed work is separated from active task categories.',
        'Task history remains visible for audit and follow-up reference.',
      ],
    },
    {
      id: 'task-states',
      title: 'Task States (Kanban View)',
      body: [
        'In the EduX To-Do module, tasks are organized into different categories based on time. These categories help users manage and prioritize their work efficiently.',
      ],
      figure: {
        src: '/assets/doc-images/todo/image5.png',
        alt: 'To-Dos Kanban states by time category',
        caption: 'Kanban view showing Inbox, Today, This Week, This Month, and Later task categories.',
      },
      subsections: [
        {
          id: 'task-categories',
          title: 'Task Categories',
          body: [
            'These categories help users understand urgency and plan work by time window.',
          ],
          bullets: [
            'Inbox: newly created tasks that are not yet scheduled.',
            'Today: tasks planned to be completed on the current day.',
            'This Week: tasks scheduled for completion within the current week.',
            'This Month: tasks planned to be completed within the current month.',
            'Later: tasks intended for future work with no immediate priority.',
          ],
        },
      ],
    },
  ],
  related: ['calendar', 'faculty', 'student'],
};

export default function ToDosPage() {
  return <DocumentPage document={toDosDocument} />;
}
